import { useEffect, useState } from 'react'
import { Check, Copy, Phone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { suppressLeavePageConfirmation } from '@/lib/hooks/useLeavePageConfirmation'
import type { PhoneDialerRequest } from '@/lib/voice-console/client-tools'

interface PhoneDialerPromptProps {
  request: PhoneDialerRequest | null
  onDismiss: () => void
}

export function PhoneDialerPrompt({ request, onDismiss }: PhoneDialerPromptProps) {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)

  useEffect(() => {
    setCopied(false)
    setCopyFailed(false)
  }, [request])

  const handleCopy = async () => {
    if (!request) return

    try {
      await navigator.clipboard.writeText(request.displayNumber)
      setCopied(true)
      setCopyFailed(false)
    } catch {
      setCopied(false)
      setCopyFailed(true)
    }
  }

  const handleOpenDialer = () => {
    suppressLeavePageConfirmation()
    setCopied(false)
    setCopyFailed(false)
  }

  const title = request?.contactName ? `Call ${request.contactName}` : 'Open phone dialer'

  return (
    <Dialog open={!!request} onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent className="border-slate-800 bg-slate-950 text-slate-100 sm:max-w-md">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-slate-100">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {request?.reason || 'The assistant prepared this phone number for you.'}
          </DialogDescription>
        </DialogHeader>

        {request && (
          <div className="space-y-4">
            <div className="rounded-md border border-slate-800 bg-slate-900/70 px-4 py-3">
              <p className="text-xs font-medium uppercase text-slate-500">Phone number</p>
              <p className="mt-1 break-all text-lg font-semibold text-slate-100">{request.displayNumber}</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                asChild
                className="flex-1 gap-2 bg-cyan-600 text-white hover:bg-cyan-500"
              >
                <a
                  href={request.telUrl}
                  onClick={handleOpenDialer}
                  aria-label={`Open phone dialer for ${request.displayNumber}`}
                >
                  <Phone className="h-4 w-4" />
                  Open dialer
                </a>
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="flex-1 gap-2 border border-slate-800 bg-slate-900/60 text-slate-100 hover:bg-slate-800 hover:text-white"
                onClick={handleCopy}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy number'}
              </Button>
            </div>

            {copyFailed && (
              <p className="text-sm text-amber-300">
                Copy failed. Select the number above if your browser does not open the dialer.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="text-slate-300 hover:bg-slate-800 hover:text-slate-100"
            onClick={onDismiss}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
